import React from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Alert, Box, Chip, CircularProgress, Collapse, Stack, TableCell, TableRow, Typography } from '@mui/material';

import { getDropdownCellSx, handleExpandRowKeyDown } from '../surcharges/surchargeTabLayout';

import EgsRoomRateList from './EgsRoomRateList';

// The row carries a leading chevron and a trailing count, so the label fills whatever is left between them.
const FIXED_CELLS_AROUND_LABEL = 2;

interface Props {
  columnCount: number;
  error: unknown;
  isExpanded: boolean;
  isLoading: boolean;
  propertyId?: string;
  roomRateIds: Array<string>;
  roomTypes: Array<App.RoomType>;
  unresolvedMatchCount: number;
  vendorId?: string;
  onToggleExpanded: () => void;
}

function getUnresolvedMatchWarning(unresolvedMatchCount: number): string {
  return unresolvedMatchCount === 1
    ? '1 Salesforce surcharge room could not be matched automatically, so these room rates may already be covered by it.'
    : `${unresolvedMatchCount} Salesforce surcharge rooms could not be matched automatically, so these room rates may already be covered by one of them.`;
}

export default function EgsUnmatchedSection({
  columnCount,
  error,
  isExpanded,
  isLoading,
  propertyId,
  roomRateIds,
  roomTypes,
  unresolvedMatchCount,
  vendorId,
  onToggleExpanded,
}: Props) {
  const showUnresolvedMatchWarning = roomRateIds.length > 0 && unresolvedMatchCount > 0;

  return (
    <>
      <TableRow
        hover
        role="button"
        sx={{
          cursor: 'pointer',
          '& .MuiTableCell-root': { bgcolor: 'grey.50' },
        }}
        tabIndex={0}
        onClick={onToggleExpanded}
        onKeyDown={(event) => handleExpandRowKeyDown(event, onToggleExpanded)}
      >
        <TableCell>{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
        <TableCell colSpan={columnCount - FIXED_CELLS_AROUND_LABEL}>
          <Typography fontWeight={700} variant="body2">
            Room rates charging an extra guest with no Salesforce surcharge
          </Typography>
        </TableCell>
        <TableCell align="center">
          {error ? (
            '—'
          ) : isLoading ? (
            <CircularProgress size={16} />
          ) : (
            <Chip label={roomRateIds.length} size="small" variant="outlined" />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={columnCount} sx={getDropdownCellSx(isExpanded)}>
          <Collapse unmountOnExit in={isExpanded} sx={{ width: '100%' }} timeout="auto">
            <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1.5 }}>
              {isLoading ? (
                <Stack alignItems="center" direction="row" justifyContent="center" spacing={1} sx={{ py: 2 }}>
                  <CircularProgress size={16} />
                  <Typography color="text.secondary" variant="body2">
                    Loading room-rate extra guest surcharges.
                  </Typography>
                </Stack>
              ) : error ? (
                <Alert severity="error">Unable to load room-rate extra guest surcharges.</Alert>
              ) : (
                <Stack spacing={1.5}>
                  {showUnresolvedMatchWarning && (
                    <Alert severity="warning">{getUnresolvedMatchWarning(unresolvedMatchCount)}</Alert>
                  )}
                  <EgsRoomRateList
                    emptyMessage="Every room rate charging an extra guest has a matching Salesforce surcharge."
                    propertyId={propertyId}
                    roomRateIds={roomRateIds}
                    roomTypes={roomTypes}
                    vendorId={vendorId}
                  />
                </Stack>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
